/**
 * Admin UI primitives.
 *
 * Same brand language as the public site — hard borders, flat shadows — but
 * calmer. This is a workspace people sit in for hours, so acid yellow is a
 * highlight here rather than a background.
 */

export function Button({
  as: Tag = 'button',
  variant = 'primary',
  size = 'md',
  className = '',
  ...props
}) {
  const variants = {
    primary: 'bg-ink text-paper hover:bg-electric',
    secondary: 'bg-paper text-ink hover:bg-acid',
    danger: 'bg-blush text-ink hover:bg-ink hover:text-paper',
    ghost: 'border-transparent bg-transparent hover:bg-acid',
  };
  const sizes = { sm: 'px-3 py-1.5 text-xs', md: 'px-5 py-2.5 text-sm', lg: 'px-7 py-3 text-base' };

  return (
    <Tag
      className={`border-3 border-ink font-bold uppercase tracking-wide transition-all disabled:cursor-not-allowed disabled:opacity-50 ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    />
  );
}

export function Card({ className = '', children, ...props }) {
  return (
    <div className={`border-brutal bg-paper ${className}`} {...props}>
      {children}
    </div>
  );
}

export function Field({ label, hint, error, required, children, htmlFor }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-bold uppercase tracking-widest">
        {label} {required && <span className="text-electric">*</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-muted">{hint}</p>}
      {error && (
        <p className="mt-1 text-xs font-bold text-electric" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

const INPUT_BASE =
  'w-full border-3 border-ink bg-paper px-3 py-2 text-sm outline-none transition-shadow focus:shadow-[3px_3px_0_0_var(--color-ink)]';

export function Input({ className = '', ...props }) {
  return <input className={`${INPUT_BASE} ${className}`} {...props} />;
}

export function Textarea({ className = '', rows = 4, ...props }) {
  return <textarea rows={rows} className={`${INPUT_BASE} ${className}`} {...props} />;
}

export function Select({ className = '', children, ...props }) {
  return (
    <select className={`${INPUT_BASE} ${className}`} {...props}>
      {children}
    </select>
  );
}

export function Checkbox({ label, className = '', ...props }) {
  return (
    <label className={`flex cursor-pointer items-center gap-3 ${className}`}>
      <input type="checkbox" className="size-5 accent-black" {...props} />
      <span className="text-sm font-semibold">{label}</span>
    </label>
  );
}

const STATUS_TONES = {
  PUBLISHED: 'bg-mint',
  DRAFT: 'bg-line',
  SCHEDULED: 'bg-acid',
  ARCHIVED: 'bg-muted text-paper',
  NEW: 'bg-acid',
  CONTACTED: 'bg-mint',
  QUALIFIED: 'bg-mint',
  PROPOSAL: 'bg-electric text-paper',
  WON: 'bg-electric text-paper',
  LOST: 'bg-line',
  SPAM: 'bg-blush',
};

export function Badge({ children, tone, className = '' }) {
  return (
    <span
      className={`inline-block border-2 border-ink px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
        STATUS_TONES[tone] ?? 'bg-paper'
      } ${className}`}
    >
      {children}
    </span>
  );
}

export function PageTitle({ title, description, action }) {
  return (
    <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-3xl">{title}</h1>
        {description && <p className="mt-2 max-w-[70ch] text-sm text-muted">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ title, description, action }) {
  return (
    <Card className="border-dashed p-12 text-center">
      <h2 className="text-xl">{title}</h2>
      {description && <p className="mx-auto mt-2 max-w-[52ch] text-sm text-muted">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
    </Card>
  );
}

export function Spinner({ label = 'Loading…' }) {
  return (
    <p className="py-12 text-center text-sm text-muted" role="status">
      {label}
    </p>
  );
}

export function ErrorNote({ error, onRetry }) {
  if (!error) return null;
  return (
    <Card className="mb-6 bg-blush/20 p-4" role="alert">
      <p className="text-sm font-semibold">{error}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
          Retry
        </Button>
      )}
    </Card>
  );
}

/** Inline save feedback — avoids a toast system for a handful of forms. */
export function SaveState({ state }) {
  if (state === 'idle') return null;
  const copy = {
    saving: 'Saving…',
    saved: 'Saved',
    error: 'Could not save',
  };
  return (
    <span
      className={`text-xs font-bold uppercase tracking-widest ${
        state === 'error' ? 'text-electric' : 'text-muted'
      }`}
      role="status"
    >
      {copy[state]}
    </span>
  );
}
